// packages/shared-i18n/applyBrandLocale.ts
import i18n, { type LanguageType } from "./i18n";

type BrandLocaleConfig = {
  meta: {
    id: string;
    name: string;
    appTitle?: string;
  };
};

const NON_ZH_LANGS = [
  "en-US",
  "ko-KR",
  "ja-JP",
  "de-DE",
  "fr-FR",
  "es-ES",
  "ru-RU",
  "tr-TR",
] as const;

let activeBrandConfig: BrandLocaleConfig | null = null;
let languageListenerAttached = false;

/** eweadn 品牌非中文界面仍展示国际名 EWEADN；其他品牌统一用 brand.json 的 name */
export function getBrandDisplayNameForLang(
  config: BrandLocaleConfig,
  lang: string
): string {
  const { name, id } = config.meta;
  if (id === "eweadn" && lang !== "zh-CN") return "EWEADN";
  return name;
}

export function getBrandDisplayName(config: BrandLocaleConfig): string {
  return getBrandDisplayNameForLang(config, i18n.language);
}

function syncInterpolationDefaults(config: BrandLocaleConfig): void {
  const brandName = getBrandDisplayNameForLang(config, i18n.language);
  const interpolation = i18n.options.interpolation ?? {};
  i18n.options.interpolation = {
    ...interpolation,
    defaultVariables: {
      ...(interpolation.defaultVariables ?? {}),
      brandName,
    },
  };
}

function patchBrandResourceBundle(config: BrandLocaleConfig): void {
  const { name, id } = config.meta;
  if (!name) return;

  syncInterpolationDefaults(config);

  const zhPatch = { common: { brandName: name } };
  i18n.addResourceBundle("zh-CN", "translation", zhPatch, true, true);

  /** 非 eweadn 品牌：仅 patch 当前已切换语言，避免为未懒加载的语言预建占位包阻塞完整语言包合并 */
  if (id !== "eweadn") {
    const current = i18n.language;
    if ((NON_ZH_LANGS as readonly string[]).includes(current)) {
      i18n.addResourceBundle(
        current,
        "translation",
        { common: { brandName: name } },
        true,
        true
      );
    }
  }
}

function resolveAppTitle(config: BrandLocaleConfig): string | undefined {
  const { appTitle, name, id } = config.meta;
  if (!appTitle) return undefined;
  if (id === "eweadn") return appTitle;
  return appTitle.replace(/EWEADN/gi, name);
}

/** 将 brand.json 的 name / appTitle 注入运行时（document.title、i18n 品牌名等） */
export function applyBrandLocale(config: BrandLocaleConfig): void {
  activeBrandConfig = config;
  const title = resolveAppTitle(config);
  if (typeof document !== "undefined" && title) {
    document.title = title;
  }

  const refreshStrings = () => {
    if (activeBrandConfig) patchBrandResourceBundle(activeBrandConfig);
  };

  const run = () => {
    refreshStrings();
    /** bindI18nStore 为空时 addResourceBundle 不会触发重渲染，需主动刷新当前语言 */
    void i18n.changeLanguage(i18n.language as LanguageType);
  };
  if (i18n.isInitialized) {
    run();
  } else {
    i18n.on("initialized", run);
  }

  if (!languageListenerAttached) {
    languageListenerAttached = true;
    i18n.on("languageChanged", refreshStrings);
  }
}
